import React, {
  useState,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { makeStyles, withStyles } from "@mui/styles";
import IconButton from "@mui/material/IconButton";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableHead from "@mui/material/TableHead";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { blue } from "@mui/material/colors";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

import WidgetConfigureDialog from "../WidgetConfigureDialog";
import AddNewTagDialog from "./AddNewTagDialog";
import TagConfigDialog from "components/TagConfig/TagConfigDialog";
import ComboBox from "components/ComboBox";
import {
  getTagInfoForStack,
  getSortedTagNamesFromInfo,
  tagsRenderGroup,
} from "components/ComboBox/tags/Utils";
import ISXUtils, { ISXContext } from "services/Utils";

import _ from "lodash";

const uuidv4 = require("uuid/v4");

const USE_TAG_CONFIG = true;
const derivedTagSeparator = "|";
const localComputeIndicator = "-lc";

const getOriginalTag = (tag) => {
  const tparts = tag.split(derivedTagSeparator);
  if (
    tparts.length > 1 &&
    tparts[tparts.length - 1].endsWith(localComputeIndicator)
  ) {
    return tparts.slice(0, -1).join(derivedTagSeparator);
  }
  return tag;
};

const useStyles = makeStyles((theme) => ({
  openIcon: {
    color: blue[500],
    cursor: "pointer",
  },
  movingAverageHeader: {
    fontSize: "0.75rem",
    fontWeight: "bolder",
    lineHeight: "1rem",
    marginTop: "0.25rem",
    marginBottom: "0.25rem",
    paddingLeft: "1rem",
    borderBottom: `2px solid`,
    backgroundColor: theme.palette.background.paper,
  },
  createNewTagButton: {
    fontSize: "0.75rem",
    textTransform: "none",
    padding: 0,
    justifyContent: "start",
  }
}));

const TagTableCell = withStyles((theme) => ({
  head: {
    paddingLeft: 12,
    paddingRight: 12,
  },
  body: {
    paddingLeft: 12,
    paddingRight: 12,
  },
}))(TableCell);

const TagAttributeCell = (props) => {
  const [addTagDialogOpen, setAddTagDialogOpen] = useState(false);
  const handleCloseAddTagDialog = () => setAddTagDialogOpen(false);

  const classes = useStyles();

  const {
    stack: stackId,
    stacks,
    showDerivedTags,
    attribute,
    setAttribute,
    filterTagsOptions,
    allowCreateNewTag,
    createNewTagLabel,
    attributeColumnHeader,
  } = props;

  const stack = stacks[stackId];

  const tagInfo = useMemo(
    () =>
      stack
        ? getTagInfoForStack(stack, filterTagsOptions, showDerivedTags)
        : {},
    [filterTagsOptions, showDerivedTags, stack]
  );

  const tagNames = useMemo(() => getSortedTagNamesFromInfo(tagInfo), [tagInfo]);

  return (
    <>
      <TagTableCell>
        <ComboBox
          value={attribute}
          options={allowCreateNewTag && stackId ? ["", ...tagNames] : tagNames}
          onChange={(_, attr) => setAttribute(attr)}
          getOptionLabel={(attr) => (attr ? tagInfo[attr]?.name ?? attr : "")}
          groupBy={(attr) => tagInfo[attr]?.avg ?? false}
          renderGroup={tagsRenderGroup}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return option === "" ? (
              <Box key={-1} component="li" {...optionProps}>
                <Button
                  color="primary"
                  fullWidth
                  className={classes.createNewTagButton}
                  onClick={(event) => {
                    setAddTagDialogOpen(true);
                    event.stopPropagation();
                  }}
                >
                  {createNewTagLabel ?? "+ Create new tag"}
                </Button>
              </Box>
            ) : (
              <Box key={option} component="li" {...optionProps}>
                {key}
              </Box>
            );
          }}
          disabled={!stackId}
        />
      </TagTableCell>
      {allowCreateNewTag && addTagDialogOpen && (
        <AddNewTagDialog
          tagType={attributeColumnHeader || "Tag"}
          open={addTagDialogOpen}
          onClose={handleCloseAddTagDialog}
          stackId={stackId}
          callback={setAttribute}
        />
      )}
    </>
  );
};

const TagRow = (props) => {
  const [tagDialogOpen, setTagDialogOpen] = useState(false);

  const { stacksSortedByName } = props;

  const tagDefined = () => {
    return props.stack && props.attribute;
  };

  const setStack = (_, stack) => {
    props.setTagProps(props.guuid, {
      stack: stack.guuid,
      attribute: null,
    });
  };

  const setAttribute = (attribute) => {
    if (attribute) {
      props.setTagProps(props.guuid, { attribute });
    }
  };

  return (
    <TableRow>
      <TagTableCell>
        {stacksSortedByName.length !== 1 ? (
          <ComboBox
            value={props.stacks[props.stack] ?? null}
            options={stacksSortedByName}
            onChange={setStack}
            getOptionKey={(option) => option.guuid}
            getOptionLabel={ISXUtils.getStackName}
          />
        ) : (
          <Typography>
            {ISXUtils.getStackName(stacksSortedByName[0])}
          </Typography>
        )}
      </TagTableCell>
      <TagAttributeCell
        {...props}
        setAttribute={setAttribute}
        setAddTagDialogOpen={props.setAddTagDialogOpen}
      />
      <TableCell style={{ paddingLeft: 0, paddingRight: 0 }}>
        {USE_TAG_CONFIG && props.stack && props.attribute && (
          <Tooltip
            title="Click to view/modify tag properties"
            onClick={(e) => {
              setTagDialogOpen(true);
            }}
            disabled={!props.stack || !props.attribute}
            slotProps={{
              tooltip: {
                sx: {
                  backgroundColor: "background.default",
                  color: "text.secondary",
                  fontSize: "0.875rem",
                  borderWidth: 0.5,
                  borderStyle: "solid",
                  border: "divider",
                },
              },
            }}
          >
            <OpenInNewIcon
              fontSize="small"
              sx={{ color: blue[500], cursor: "pointer" }}
            />
          </Tooltip>
        )}
      </TableCell>
      <TagTableCell>
        {props.deleteTag && (
          <IconButton onClick={() => props.deleteTag(props.guuid)} size="large">
            <CancelIcon />
          </IconButton>
        )}
        {props.addTag && (
          <IconButton onClick={props.addTag} disabled={!tagDefined()} size="large">
            <AddCircleIcon />
          </IconButton>
        )}
      </TagTableCell>
      {tagDialogOpen && (
        <TagConfigDialog
          open={tagDialogOpen}
          setOpen={setTagDialogOpen}
          tag={{
            guuid: props.guuid,
            stack: props.stack,
            attribute: getOriginalTag(props.attribute),
          }}
        />
      )}
    </TableRow>
  );
};

const WidgetTagsData = (props) => {
  const isxContext = useContext(ISXContext);

  const { maxTags, constrainTagStack } = props;
  const tags = Object.values(props.tags);

  const stacksSortedByName = useMemo(
    () =>
      props.stacks
        ? (props.allowCreateNewTag
          ? Object.values(props.stacks)
          : Object.values(props.stacks).filter(
            (stack) =>
              ISXUtils.filterStackTags(stack, props.filterTagsOptions)
                .length > 0
          )
        ).sort((a, b) =>
          ISXUtils.strcasecmp(
            ISXUtils.getStackName(a),
            ISXUtils.getStackName(b)
          )
        )
        : [],
    [props.allowCreateNewTag, props.filterTagsOptions, props.stacks]
  );

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TagTableCell>{isxContext.labels.deviceCapitalized}</TagTableCell>
          <TagTableCell>
            {props.attributeColumnHeader ?? "Attribute"}
          </TagTableCell>
          <TagTableCell />
          <TagTableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {tags.map((tag, idx) => {
          const addTag =
            idx === tags.length - 1 && (!maxTags || tags.length < maxTags)
              ? props.addTag
              : null;
          const deleteTag = maxTags !== 1 ? props.deleteTag : null;
          let stacks = null;
          if (constrainTagStack && tags.length > 1) {
            let tstacks = tags.map((t) => t.stack).filter((t) => !!t);
            tstacks = new Set(tstacks);
            if (tstacks.size === 1) {
              const stid = tstacks.values().next().value;
              const st = (props.stacks || {})[stid];
              stacks = st && { [stid]: st };
            }
          }
          if (!stacks) {
            stacks = props.stacks || {};
          }
          return (
            <TagRow
              {...props}
              key={tag.guuid}
              stacks={stacks}
              stacksSortedByName={stacksSortedByName}
              {...tag}
              deleteTag={deleteTag}
              addTag={addTag}
            />
          );
        })}
      </TableBody>
    </Table>
  );
};

const WidgetTagsConfigureDialog = (props) => {
  const [tags, setTags] = useState();
  const [initialized, setInitialized] = useState(false);
  const dirty = useRef(false);

  const createEmptyTag = useCallback(() => {
    const guuid = uuidv4();
    const stacks = !!props.stacks ? Object.values(props.stacks) : [];
    let stack = stacks.length === 1 ? stacks[0].guuid : "";
    if (!stack && (props.prepopulateTagStack || props.constrainTagStack)) {
      let tstacks = Object.values(tags || {})
        .map((t) => t.stack)
        .filter((t) => !!t);
      tstacks = new Set(tstacks);
      if (tstacks.size === 1) {
        stack = tstacks.values().next().value;
      }
    }
    return {
      guuid: guuid,
      stack: stack,
      attribute: null,
    };
  }, [props.constrainTagStack, props.prepopulateTagStack, props.stacks, tags]);

  useEffect(() => {
    if (!initialized) {
      const widget = props.widget;
      const tags = widget.tags || [];
      let tagsMap = {};
      if (tags.length > 0) {
        tags.forEach((tag) => {
          tagsMap[tag.guuid] = tag;
        });
      } else {
        const tag = createEmptyTag();
        tagsMap[tag.guuid] = tag;
      }
      setTags(tagsMap);
      setInitialized(true);
    }
  }, [initialized, props.widget, createEmptyTag]);

  const prepareTagsForWriteback = () => {
    let tagsList = Object.values(tags);
    if (tagsList.length > 0 && !tagDefined(_.last(tagsList))) {
      tagsList.pop();
    }
    return { tags: tagsList };
  };

  const titleBuilder = (data) => {
    let allStacks = new Set();
    data.tags.forEach((tag) => {
      allStacks.add(tag.stack);
    });
    const allAttributes = data.tags.map((tag) => tag.attribute);
    let title;
    if (allStacks.size === 1) {
      const stackid = allStacks.values().next().value;
      const stack = props.stacks[stackid] || {};
      const stackTitle =
        stack?.name || JSON.parse(stack.dev_session || "{}")["stack.label"];
      const attrs = allAttributes
        .map((attr) => ISXUtils.getTagDisplayText(attr, stack.data_config))
        .join(", ");
      title = stackTitle + " - " + attrs;
    } else {
      title = "Widget";
    }
    return title;
  };

  const areTagsValid = () => {
    const tagsList = Object.values(tags);
    return tagsList.length > 0 && tagDefined(_.last(tagsList));
  };

  const setTagProps = (guuid, props) => {
    let tag = tags[guuid];
    if (tag) {
      tag = {
        ...tag,
        ...props,
      };
      setTags({ ...tags, [guuid]: tag });
      dirty.current = true;
    }
  };

  const addTag = () => {
    const tag = createEmptyTag();
    setTags({
      ...tags,
      [tag.guuid]: tag,
    });
  };

  const deleteTag = (guuid) => {
    const tagsCopy = { ...tags };
    delete tagsCopy[guuid];
    if (Object.keys(tagsCopy).length === 0) {
      const tag = createEmptyTag();
      tagsCopy[tag.guuid] = tag;
    }
    setTags(tagsCopy);
    dirty.current = true;
  };

  const tagDefined = (tag) => {
    return tag.stack && tag.attribute;
  };

  return (
    initialized && (
      <WidgetConfigureDialog
        {...props}
        dataProperty="tags"
        tagsSelected={tags}
        dataLabel={props.maxTags !== 1 ? "tags" : "tag"}
        prepareDataForWriteback={prepareTagsForWriteback}
        isDataValid={areTagsValid}
        titleBuilder={titleBuilder}
        dataDirty={dirty}
      >
        <WidgetTagsData
          {...props}
          tags={tags}
          addTag={addTag}
          deleteTag={deleteTag}
          setTagProps={setTagProps}
        />
      </WidgetConfigureDialog>
    )
  );
};

export default WidgetTagsConfigureDialog;
